import type { EducationalContentTitle } from "@v2/features/infra/analytics/events";

import * as React from "react";

import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { AllExperimentsTable } from "@v1/features/experiments/AllExperimentsTable";
import { CreateExperimentButton } from "@v1/features/experiments/components/CreateExperimentButton";
import { HelpDocumentationIcon } from "@v1/features/experiments/components/HelpDocumentationIcon";
import { ExperimentsLayout } from "@v1/features/experiments/Layout";
import ExperimentsSetup from "@v1/features/experiments/Setup";
import { useExperimentApi } from "@v1/features/experiments/utils";
import { Loader } from "@v2/components/Loader";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import {
  useCurrentWorkspace,
  useCurrentWorkspaceId,
} from "@v2/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { docs } from "@v2/utils/docs";
import { routes } from "@v2/utils/router";
import { Settings } from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";

const ExperimentActions: React.FC<{
  showCreateExperimentButton: boolean;
}> = ({ showCreateExperimentButton }) => {
  const workspaceId = useCurrentWorkspaceId() ?? undefined;
  const projectId = useCurrentProjectId();

  const navigate = useNavigate();
  return (
    <div className="flex flex-row gap-2">
      <HelpDocumentationIcon />
      {/* NOTE (Max, 2025-06-05): This is the experiment-level settings, which are only
      relevant in the non-shops world. In the Shops UI, these experiment settings will be directly
      in the shops settings, so no need to show it on this header. */}
      {Boolean(workspaceId) && (
        <DeprecatedIconButton
          variant="secondary"
          size="base"
          icon={<Settings size={16} className="text-default" />}
          onClick={() => {
            navigate(
              projectId
                ? generatePath(routes.shops.settings.shops.abtesting, {
                    projectId,
                  })
                : generatePath(routes.workspace.experiments.settings, {
                    workspaceId,
                  }),
            );
          }}
        />
      )}

      {showCreateExperimentButton && <CreateExperimentButton />}
    </div>
  );
};

type EducationSectionContainerProps = {
  title: EducationalContentTitle;
  image: string;
  link: string;
};

const EducationSectionContainer: React.FC<EducationSectionContainerProps> = ({
  title,
  image,
  link,
}) => {
  const logEvent = useLogAnalytics();
  return (
    <div
      className="border-0.5 border-border bg-sheet hover:bg-surface flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md py-3"
      onClick={() => {
        window.open(link, "_blank");
        logEvent("experiment.educationalContent.clicked", {
          from: "allExperimentsTable",
          contentTitle: title,
        });
      }}
    >
      <img src={image} alt={title} />
      <span>{title}</span>
    </div>
  );
};

const EDUCATION_SECTIONS: Array<EducationSectionContainerProps> = [
  {
    title: "Get Started",
    image: "/images/experiments/education/how-to-get-started.svg",
    link: docs.abTesting.education.getStarted,
  },
  {
    title: "Explore Top Tests",
    image: "/images/experiments/education/explore-top-tests.svg",
    link: docs.abTesting.education.exploreTopTests,
  },
  {
    title: "Why A/B Testing Works",
    image: "/images/experiments/education/why-you-should-test.svg",
    link: docs.abTesting.education.whyABTestingWorks,
  },
  {
    title: "Frequently Asked Questions",
    image: "/images/experiments/education/frequently-asked-questions.svg",
    link: docs.abTesting.education.frequentlyAskedQuestions,
  },
];

const EducationSection: React.FC = () => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-medium">
      {EDUCATION_SECTIONS.map((section) => (
        <EducationSectionContainer
          key={section.title}
          title={section.title}
          image={section.image}
          link={section.link}
        />
      ))}
    </div>
  );
};

const Experiments: React.FC = () => {
  const workspaceName = useCurrentWorkspace()?.name;
  const { project } = useCurrentProjectContext();
  const projectName = project?.name;

  const {
    list: { data, isFetching: isFetchingExperiments },
    links: { data: links, isFetching: isFetchingLinks },
  } = useExperimentApi();

  if (isFetchingExperiments || isFetchingLinks || !links || !data) {
    return <Loader />;
  }

  const experiments = data.experiments;
  const hasExperiments = experiments.length > 0;

  return (
    <ExperimentsLayout
      headerTitle={`${projectName ? projectName : workspaceName}'s Tests`}
      headerActions={
        <ExperimentActions showCreateExperimentButton={hasExperiments} />
      }
    >
      <EducationSection />
      {hasExperiments ? (
        <AllExperimentsTable experiments={experiments} links={links} />
      ) : (
        <ExperimentsSetup />
      )}
    </ExperimentsLayout>
  );
};

export default Experiments;
